
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { registerSchema } from "./schema";
import { WaitlistFormValues } from "@/components/waitlist/schema";
import PersonalInfoFields from "./PersonalInfoFields";
import PasswordField from "../common/PasswordField";
import TermsCheckbox from "./TermsCheckbox";
import { RegistrationSocialLogin } from "./RegistrationSocialLogin";
import { useRegistration } from "@/hooks/use-registration";

interface CompactRegistrationFormProps {
  fromThankYou?: boolean;
}

const CompactRegistrationForm: React.FC<CompactRegistrationFormProps> = ({ fromThankYou = false }) => {
  const [waitlistData, setWaitlistData] = useState<Partial<WaitlistFormValues> | null>(null);
  const { isLoading, handleSignUp } = useRegistration({ 
    fromThankYou: fromThankYou,
    waitlistData: true
  });
  
  const form = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      agreeToTerms: false
    }
  });

  useEffect(() => {
    const savedFormData = sessionStorage.getItem('waitlistFormData');
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        setWaitlistData(parsedData);
        
        form.setValue('firstName', parsedData.firstName || '');
        form.setValue('lastName', parsedData.lastName || '');
        form.setValue('email', parsedData.email || '');
      } catch (e) {
        console.error('Error parsing waitlist form data:', e);
      }
    }
  }, [form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSignUp)} className="space-y-4">
        <PersonalInfoFields 
          control={form.control} 
          defaultValues={{
            firstName: waitlistData?.firstName || '',
            lastName: waitlistData?.lastName || '',
            email: waitlistData?.email || ''
          }}
        />
        <PasswordField 
          control={form.control} 
          name="password" 
          label="Password" 
          placeholder="Create a strong password" 
          autoComplete="new-password"
          helpText="Password must be at least 8 characters"
        />
        <TermsCheckbox control={form.control} />

        <Button 
          type="submit" 
          className="w-full h-11 mt-6 bg-grandpal-green hover:bg-grandpal-green/90 text-white font-medium" 
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : "Create Account"}
        </Button>
      </form>

      <RegistrationSocialLogin />
    </Form>
  );
};

export default CompactRegistrationForm;
